/**
 * NearPoiFilterSection
 * ====================
 * A custom filter pill for "W pobliżu POI" (Near POI proximity filter).
 *
 * This is an example of Option B custom pill (see filter-pill-primitives.tsx architecture docs):
 * - Uses CustomFilterPill from @ooh/ui as the pill trigger button shell
 * - Wraps it with a Popover containing category select + radius preset buttons
 * - Registered in FilterPanel via extraHiddenItems + renderExtraPill (see map-filter-toolbar.tsx)
 * - State lives OUTSIDE FilterPanel's value Record (in URL params via usePoiFilterParams)
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import { Button } from '@ooh/ui/components/button';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption,
} from '@ooh/ui/components/searchable-select-field';
import { Label } from '@ooh/ui/components/label';
import {
  CustomFilterPill,
  PILL_VALUE_CLASS,
  PillWrapper,
} from '@ooh/ui/components/filter-pill-primitives';
import { poiQueries } from '../api/queries';
import type { PoiFilterParams } from '../../../hooks/use-poi-filter-params';
import { getCategoryLabel } from '../utils/category-label';
import {
  POI_RADIUS_OPTIONS_M,
  formatPoiRadiusLabel,
  encodeCategoryKey,
  decodeCategoryKey,
} from '../utils/poi-constants';

const RADIUS_OPTIONS = POI_RADIUS_OPTIONS_M.map((m) => ({
  value: m,
  label: formatPoiRadiusLabel(m),
}));

interface NearPoiFilterSectionProps {
  value: PoiFilterParams;
  onChange: (value: PoiFilterParams) => void;
  /** Called when the user clears the filter — removes the pill from FilterPanel */
  onHide?: () => void;
  /** Resolved POI name for per-POI mode label (optional, falls back to generic label) */
  nearPoiName?: string;
}

// eslint-disable-next-line complexity
export function NearPoiFilterSection({
  value,
  onChange,
  onHide,
  nearPoiName,
}: NearPoiFilterSectionProps) {
  const { auth } = useRouteContext({ from: '__root__' });
  const { t } = useTranslation('map');

  const [open, setOpen] = React.useState(false);

  const { data: categoriesData } = useQuery(poiQueries.categories(auth));
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const categoryOptions: SearchableSelectFieldOption[] = categories.map((cat) => ({
    value: encodeCategoryKey(cat.osm_key, cat.osm_value),
    label: getCategoryLabel(cat, t),
  }));

  // Draft state — local to the popover, committed on Apply
  const [draftKey, setDraftKey] = React.useState('');
  const [draftValue, setDraftValue] = React.useState('');
  const [draftRadius, setDraftRadius] = React.useState<number | null>(null);

  // Sync draft from committed value when popover opens
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftKey(value.nearKey ?? '');
      setDraftValue(value.nearValue ?? '');
      setDraftRadius(value.nearRadius ?? null);
    }
    setOpen(next);
  };

  const isPerPoiActive = Boolean(value.nearPoiId && value.nearRadius);
  const isActive = Boolean(value.nearKey && value.nearValue && value.nearRadius) || isPerPoiActive;
  const canApply = Boolean(draftKey && draftValue && draftRadius != null);

  const handleCategoryChange = (catKey: string) => {
    const [key, val] = decodeCategoryKey(catKey);
    setDraftKey(key ?? '');
    setDraftValue(val ?? '');
  };

  const handleApply = () => {
    if (!canApply || draftRadius == null) return;
    onChange({ nearKey: draftKey, nearValue: draftValue, nearRadius: draftRadius });
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({});
    onHide?.();
  };

  const activeCategory =
    isActive && !isPerPoiActive
      ? categories.find((c) => c.osm_key === value.nearKey && c.osm_value === value.nearValue)
      : null;
  const activeRadius = isActive
    ? (RADIUS_OPTIONS.find((r) => r.value === value.nearRadius)?.label ??
      `${value.nearRadius ?? ''}m`)
    : null;
  const perPoiPillLabel = isPerPoiActive
    ? t('pois.filterSection.nearPoiPill', {
        radius: activeRadius ?? '',
        name: nearPoiName ?? t('pois.filterSection.unknownPoi'),
      })
    : null;

  // In per-POI mode the popover serves no purpose — render the chip without a Popover wrapper.
  if (isPerPoiActive) {
    return (
      <PillWrapper label={t('pois.filterSection.label')} showLabel={isActive}>
        <CustomFilterPill
          label={t('pois.filterSection.label')}
          isActive={isActive}
          onClear={handleClear}
          clearAriaLabel={t('filters.clearAllLabel')}
          clearDataTestId="near-poi-pill-clear" // eslint-disable-line i18next/no-literal-string
          data-testid="near-poi-pill-trigger"
        >
          <span className={PILL_VALUE_CLASS}>{perPoiPillLabel}</span>
        </CustomFilterPill>
      </PillWrapper>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PillWrapper label={t('pois.filterSection.label')} showLabel={isActive}>
        <PopoverTrigger asChild>
          <CustomFilterPill
            label={t('pois.filterSection.label')}
            isActive={isActive}
            onClear={handleClear}
            clearAriaLabel={t('filters.clearAllLabel')}
            clearDataTestId="near-poi-pill-clear" // eslint-disable-line i18next/no-literal-string
            data-testid="near-poi-pill-trigger"
            aria-expanded={open}
          >
            <span className={PILL_VALUE_CLASS}>
              {activeCategory ? getCategoryLabel(activeCategory, t) : value.nearKey}
            </span>
            <span className={PILL_VALUE_CLASS}>{activeRadius}</span>
          </CustomFilterPill>
        </PopoverTrigger>
      </PillWrapper>

      <PopoverContent className="w-72 p-4" align="start">
        <div className="flex flex-col gap-4">
          {/* Category */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-[12px] font-medium">
              {t('pois.filterSection.categoryLabel')}
            </Label>
            <SearchableSelectField
              className="h-8 text-[12px]"
              options={categoryOptions}
              value={draftKey && draftValue ? encodeCategoryKey(draftKey, draftValue) : ''}
              onChange={handleCategoryChange}
              placeholder={t('pois.filterSection.categoryPlaceholder')}
              searchPlaceholder={t('pois.filterSection.categorySearchPlaceholder')}
              emptyText={t('pois.filterSection.categoryNoResults')}
              data-testid="near-poi-category-select"
            />
          </div>

          {/* Radius */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-[12px] font-medium">{t('pois.filterSection.radiusLabel')}</Label>
            <div className="flex gap-2 flex-wrap">
              {RADIUS_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={draftRadius === opt.value ? 'default' : 'outline'}
                  size="xs"
                  onClick={() => setDraftRadius(opt.value)}
                  data-testid={`near-poi-radius-${opt.value}`}
                  // eslint-disable-next-line i18next/no-literal-string
                  data-selected={draftRadius === opt.value ? 'true' : 'false'}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Apply */}
          <Button
            size="xs"
            disabled={!canApply}
            onClick={handleApply}
            data-testid="near-poi-apply-button"
            fullWidth
          >
            {t('pois.filterSection.applyLabel')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
